import * as React from "react";
import clx from "classnames";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FaIcon } from "@/lib/fa";
import { Spinner } from "../feedback"
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  text?: React.ReactNode;
  size?: "xs" | "sm" | "md" | "lg";
  variant?:
    | "primary"
    | "secondary"
    | "tertiary"
    | "neutral"
    | "accent"
    | "link"
    | "error"
    | "success"
    | "warning";
  mode?: "fill" | "outline";
  loading?: boolean;
  disabled?: boolean;
  faLeadingIcon?: IconProp;
  faTrailingIcon?: IconProp;
}

const sizeClasses = {
  xs: "tw-h-6 tw-text-xs tw-px-2",
  sm: "tw-h-8 tw-text-sm tw-px-3",
  md: "tw-h-12 tw-text-md tw-px-4",
  lg: "tw-h-16 tw-text-lg tw-px-5",
};

const iconSize = {
  xs: "tw-w-4 tw-h-4",
  sm: "tw-w-4 tw-h-4",
  md: "tw-w-5 tw-h-5",
  lg: "tw-w-6 tw-h-6",
};

const baseClasses =
  "tw-relative tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-md tw-font-medium tw-transition-all tw-duration-150 tw-focus:tw-outline-none tw-focus:tw-ring disabled:tw-opacity-50 disabled:tw-pointer-events-none";

const fill = {
  primary: "tw-bg-primary tw-text-content-primary hover:tw-bg-primary/90",
  secondary: "tw-bg-secondary tw-text-content-secondary hover:tw-bg-secondary/90",
  tertiary: "tw-bg-base-200 tw-text-content-base hover:tw-bg-base-300",
  neutral: "tw-bg-neutral tw-text-base-100 hover:tw-bg-neutral/90",
  accent: "tw-bg-accent tw-text-content-primary hover:tw-bg-accent/90",
  link: "tw-bg-transparent tw-text-primary hover:tw-underline",
  error: "tw-bg-error tw-text-content-primary hover:tw-bg-error/90",
  success: "tw-bg-success tw-text-content-primary hover:tw-bg-success/90",
  warning: "tw-bg-warning tw-text-amber hover:tw-bg-warning/90",
};

const outline = {
  primary: "tw-border tw-border-primary tw-text-primary hover:tw-bg-primary/10",
  secondary: "tw-border tw-border-secondary tw-text-secondary hover:tw-bg-secondary/20",
  tertiary: "tw-border tw-border-base-300 tw-text-base hover:tw-bg-base-200/60",
  neutral: "tw-border tw-border-neutral tw-text-neutral hover:tw-bg-neutral/10",
  accent: "tw-border tw-border-accent tw-text-accent hover:tw-bg-accent/10",
  link: "tw-border-none tw-text-primary hover:tw-underline",
  error: "tw-border tw-border-error tw-text-error hover:tw-bg-error/10",
  success: "tw-border tw-border-success tw-text-success hover:tw-bg-success/10",
  warning: "tw-border tw-border-warning tw-text-warning hover:tw-bg-warning/10",
};

export const Button: React.FC<ButtonProps> = ({
  text,
  size = "md",
  variant = "primary",
  mode = "fill",
  loading = false,
  disabled = false,
  faLeadingIcon,
  faTrailingIcon,
  children,
  className,
  ...props
}) => {
  const variantClasses = mode === "fill" ? fill[variant] : outline[variant];

  return (
    <button
      className={clx(
        baseClasses,
        sizeClasses[size],
        variantClasses,
        (loading || disabled) && "tw-opacity-60 tw-cursor-not-allowed",
        className
      )}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
        {loading && <Spinner className={clx("tw-opacity-80 tw-absolute tw-w-100 tw-h-[60%]", iconSize[size])}  />}

        {faLeadingIcon ? (
            <FaIcon icon={faLeadingIcon} className={iconSize[size]} />
        ) : null}

        {/* text prop ma pierwszeństwo */}
        {text ?? children}

        {faTrailingIcon ? (
            <FaIcon icon={faTrailingIcon} className={iconSize[size]} />
        ) : null}
    </button>
  );
};
